// src/pages/admin/AdminProductForm.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Box, TextField, Button, Alert,
  FormControl, InputLabel, Select, MenuItem, Divider,
  IconButton, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, Paper, Tooltip, CircularProgress, Tabs, Tab
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/api';
import { getFullImageUrl } from '../../utils/urlHelpers';

const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [tab, setTab] = useState(0);

  // Temel alanlar
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [oemCode, setOemCode] = useState('');
  const [slug, setSlug] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [metaKeywords, setMetaKeywords] = useState('');
  const [productSubTypeId, setProductSubTypeId] = useState('');

  // İlişkili veriler
  const [subTypes, setSubTypes] = useState([]);
  const [images, setImages] = useState([]);
  const [compatibilities, setCompatibilities] = useState([]);

  // Araç uyumluluk ekleme
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [seriesList, setSeriesList] = useState([]);
  const [selBrand, setSelBrand] = useState('');
  const [selModel, setSelModel] = useState('');
  const [selSeries, setSelSeries] = useState('');

  const [error, setError] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('adminToken');
  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchProductData = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/products/${id}`);
      const p = res.data;
      setName(p.name); setDescription(p.description); setImageUrl(p.imageUrl || '');
      setOemCode(p.oemCode || ''); setSlug(p.slug || '');
      setMetaTitle(p.metaTitle || ''); setMetaDescription(p.metaDescription || '');
      setMetaKeywords(p.metaKeywords || ''); setProductSubTypeId(p.productSubTypeId);
    } catch { setError('Ürün bilgileri alınamadı.'); }

    try {
      const res = await axiosInstance.get(`/productimages?productId=${id}`);
      setImages(res.data);
    } catch {}

    try {
      const res = await axiosInstance.get(`/productcompatibilities?productId=${id}`);
      setCompatibilities(res.data);
    } catch {}
  }, [id]);

  useEffect(() => {
    axiosInstance.get('/productsubtypes').then(r => setSubTypes(r.data)).catch(() => {});
    axiosInstance.get('/vehiclebrands').then(r => setBrands(r.data)).catch(() => {});
    if (isEdit) fetchProductData();
  }, [isEdit, fetchProductData]);

  // Ad değişince slug otomatik üret (yeni ürün)
  const handleNameChange = (val) => {
    setName(val);
    if (!isEdit && !slug) {
      setSlug(val.toLowerCase()
        .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's')
        .replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
        .replace(/\s+/g, '-').replace(/-+/g, '-').trim());
    }
  };

  // --- Fotoğraf Yükleme ---
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadError('');
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axiosInstance.post('/fileupload/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data', ...authHeader }
        });
        const fileName = res.data.fileName;
        if (isEdit) {
          const isMain = images.length === 0;
          const imgRes = await axiosInstance.post('/productimages', {
            productId: Number(id), imageUrl: fileName, isMain, displayOrder: images.length
          }, { headers: authHeader });
          setImages(prev => [...prev, imgRes.data]);
          if (isMain) setImageUrl(fileName);
        } else {
          // Yeni ürün: geçici listede tut
          setImages(prev => [...prev, { tempId: Date.now() + Math.random(), imageUrl: fileName, isMain: prev.length === 0, displayOrder: prev.length }]);
          if (images.length === 0) setImageUrl(fileName);
        }
      } catch {
        setUploadError('Bazı dosyalar yüklenemedi.');
      }
    }
  };

  const handleSetMain = async (img) => {
    if (isEdit) {
      await axiosInstance.put(`/productimages/${img.id}/setmain`, {}, { headers: authHeader });
      setImages(prev => prev.map(i => ({ ...i, isMain: i.id === img.id })));
    } else {
      setImages(prev => prev.map(i => ({ ...i, isMain: i.tempId === img.tempId })));
    }
    setImageUrl(img.imageUrl);
  };

  const handleDeleteImage = async (img) => {
    if (isEdit && img.id) {
      await axiosInstance.delete(`/productimages/${img.id}`, { headers: authHeader });
    }
    setImages(prev => prev.filter(i => (isEdit ? i.id !== img.id : i.tempId !== img.tempId)));
  };

  // --- Araç Uyumluluk ---
  const handleBrandChange = async (val) => {
    setSelBrand(val); setSelModel(''); setSelSeries(''); setSeriesList([]);
    if (val) {
      const res = await axiosInstance.get(`/vehiclemodels?brandId=${val}`);
      setModels(res.data);
    } else { setModels([]); }
  };

  const handleModelChange = async (val) => {
    setSelModel(val); setSelSeries('');
    if (val) {
      const res = await axiosInstance.get(`/vehicleseries?modelId=${val}`);
      setSeriesList(res.data);
    } else { setSeriesList([]); }
  };

  const handleAddCompat = async () => {
    if (!selSeries) return;
    const already = compatibilities.some(c => c.vehicleSeriesId === selSeries);
    if (already) return;
    const series = seriesList.find(s => s.id === selSeries);
    const model = models.find(m => m.id === selModel);
    const brand = brands.find(b => b.id === selBrand);

    if (isEdit) {
      const res = await axiosInstance.post('/productcompatibilities', {
        productId: Number(id), vehicleSeriesId: selSeries
      }, { headers: authHeader });
      setCompatibilities(prev => [...prev, { ...res.data, seriesName: series?.name, modelName: model?.name, brandName: brand?.name, yearStart: series?.yearStart, yearEnd: series?.yearEnd }]);
    } else {
      setCompatibilities(prev => [...prev, {
        tempId: Date.now(), vehicleSeriesId: selSeries,
        seriesName: series?.name, modelName: model?.name, brandName: brand?.name,
        yearStart: series?.yearStart, yearEnd: series?.yearEnd
      }]);
    }
  };

  const handleRemoveCompat = async (c) => {
    if (isEdit && c.id) {
      await axiosInstance.delete(`/productcompatibilities/${c.id}`, { headers: authHeader });
    }
    setCompatibilities(prev => prev.filter(x => (isEdit ? x.id !== c.id : x.tempId !== c.tempId)));
  };

  // --- Kaydet ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const mainImg = images.find(i => i.isMain);
    const data = {
      name, description,
      imageUrl: mainImg ? mainImg.imageUrl : imageUrl,
      oemCode, slug, metaTitle, metaDescription, metaKeywords, productSubTypeId
    };
    try {
      let productId = id ? Number(id) : null;
      if (isEdit) {
        await axiosInstance.put(`/products/${id}`, data, { headers: authHeader });
      } else {
        const res = await axiosInstance.post('/products', data, { headers: authHeader });
        productId = res.data.id;
        // Geçici görselleri kaydet
        for (const img of images) {
          await axiosInstance.post('/productimages', {
            productId, imageUrl: img.imageUrl, isMain: img.isMain, displayOrder: img.displayOrder
          }, { headers: authHeader });
        }
        // Geçici uyumlulukları kaydet
        for (const c of compatibilities) {
          await axiosInstance.post('/productcompatibilities', {
            productId, vehicleSeriesId: c.vehicleSeriesId
          }, { headers: authHeader });
        }
      }
      navigate('/admin/products');
    } catch (err) {
      setError('Kayıt başarısız: ' + (err.response?.data || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          {isEdit ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Temel Bilgiler" />
        <Tab label="Fotoğraflar" />
        <Tab label="Araç Uyumluluğu" />
        <Tab label="SEO" />
      </Tabs>

      <form onSubmit={handleSubmit}>
        {/* TAB 0: Temel Bilgiler */}
        {tab === 0 && (
          <Box>
            <TextField label="Ürün Adı *" fullWidth margin="normal" required
              value={name} onChange={e => handleNameChange(e.target.value)} />
            <TextField label="Açıklama" fullWidth margin="normal" multiline rows={5}
              value={description} onChange={e => setDescription(e.target.value)} />
            <TextField label="OEM / Parça Kodu" fullWidth margin="normal"
              value={oemCode} onChange={e => setOemCode(e.target.value)}
              helperText="Orijinal parça numarası (opsiyonel)" />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Ürün Kategorisi (Alt Tip)</InputLabel>
              <Select label="Ürün Kategorisi (Alt Tip)" value={productSubTypeId}
                onChange={e => setProductSubTypeId(e.target.value)}>
                {subTypes.map(st => (
                  <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* TAB 1: Fotoğraflar */}
        {tab === 1 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Fotoğraf Yükle (birden fazla seçebilirsiniz)
            </Typography>
            <Button variant="outlined" component="label" startIcon={<AddIcon />}>
              Dosya Seç
              <input type="file" hidden multiple accept="image/*" onChange={handleFileUpload} />
            </Button>
            {uploadError && <Alert severity="error" sx={{ mt: 1 }}>{uploadError}</Alert>}

            {images.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {images.map(img => (
                  <Box key={img.id || img.tempId} sx={{
                    position: 'relative', border: img.isMain ? '3px solid #f08a0c' : '2px solid #ddd',
                    borderRadius: 2, overflow: 'hidden', width: 150
                  }}>
                    <img src={getFullImageUrl(img.imageUrl)} alt="" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                    <Box sx={{ p: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Tooltip title={img.isMain ? 'Ana görsel' : 'Ana görsel yap'}>
                        <IconButton size="small" color={img.isMain ? 'warning' : 'default'} onClick={() => handleSetMain(img)}>
                          {img.isMain ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      {img.isMain && <Chip label="Ana" size="small" color="warning" />}
                      <Tooltip title="Sil">
                        <IconButton size="small" color="error" onClick={() => handleDeleteImage(img)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* TAB 2: Araç Uyumluluğu */}
        {tab === 2 && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Uyumlu araç serileri ekleyin</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel>Marka</InputLabel>
                <Select label="Marka" value={selBrand} onChange={e => handleBrandChange(e.target.value)}>
                  <MenuItem value="">Seçin</MenuItem>
                  {brands.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 160 }} disabled={!selBrand}>
                <InputLabel>Model</InputLabel>
                <Select label="Model" value={selModel} onChange={e => handleModelChange(e.target.value)}>
                  <MenuItem value="">Seçin</MenuItem>
                  {models.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }} disabled={!selModel}>
                <InputLabel>Seri / Nesil</InputLabel>
                <Select label="Seri / Nesil" value={selSeries} onChange={e => setSelSeries(e.target.value)}>
                  <MenuItem value="">Seçin</MenuItem>
                  {seriesList.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name} ({s.yearStart}–{s.yearEnd || '...'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCompat} disabled={!selSeries}>
                Ekle
              </Button>
            </Box>

            {compatibilities.length > 0 ? (
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>Marka</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Seri / Nesil</TableCell>
                      <TableCell>Yıllar</TableCell>
                      <TableCell align="right">Kaldır</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compatibilities.map(c => (
                      <TableRow key={c.id || c.tempId}>
                        <TableCell>{c.brandName}</TableCell>
                        <TableCell>{c.modelName}</TableCell>
                        <TableCell>{c.seriesName}</TableCell>
                        <TableCell>{c.yearStart}–{c.yearEnd || 'günümüz'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="error" onClick={() => handleRemoveCompat(c)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            ) : (
              <Typography color="text.secondary">Henüz araç uyumluluğu eklenmemiş.</Typography>
            )}
          </Box>
        )}

        {/* TAB 3: SEO */}
        {tab === 3 && (
          <Box>
            <TextField label="URL Slug" fullWidth margin="normal"
              value={slug} onChange={e => setSlug(e.target.value)}
              helperText='Örn: "renault-clio-4-fren-diski" — otomatik üretilir, düzenleyebilirsiniz.' />
            <TextField label="Meta Başlık" fullWidth margin="normal"
              value={metaTitle} onChange={e => setMetaTitle(e.target.value)}
              helperText="Tarayıcı sekmesinde görünür. Boş bırakırsanız ürün adı kullanılır." />
            <TextField label="Meta Açıklama" fullWidth margin="normal" multiline rows={3}
              value={metaDescription} onChange={e => setMetaDescription(e.target.value)}
              helperText="Google arama sonuçlarında görünen açıklama (max 160 karakter)." />
            <TextField label="Anahtar Kelimeler" fullWidth margin="normal"
              value={metaKeywords} onChange={e => setMetaKeywords(e.target.value)}
              helperText='Virgülle ayırın. Örn: "fren diski, clio 4, renault"' />
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={saving}>
            {saving ? <CircularProgress size={24} /> : (isEdit ? 'Güncelle' : 'Oluştur')}
          </Button>
          <Button variant="outlined" fullWidth onClick={() => navigate('/admin/products')}>
            İptal
          </Button>
        </Box>
      </form>
    </Container>
  );
};

export default AdminProductForm;
